<template>
  <div class="card card-flush shadow-sm">
    <div class="card-header border-0 pt-5">
      <h3 class="card-title align-items-start flex-column">
        <span class="card-label fw-bold fs-3 mb-1">Jurnal de cumpărări & vânzări</span>
      </h3>
      <div class="card-toolbar d-flex gap-2" v-if="jurnalData">
        <button type="button" class="btn btn-sm btn-light-info fw-bold" @click="exportPDF('preview')">
          <i class="fa-solid fa-eye me-1"></i> Previzualizare PDF
        </button>
        <button type="button" class="btn btn-sm btn-light-danger fw-bold" @click="exportPDF('save')">
          <i class="fa-solid fa-download me-1"></i> Descarcă PDF
        </button>
      </div>
    </div>

    <div class="card-body py-3">
      <!-- Filtre perioadă (Fisa Cont style) -->
      <div class="mb-5 border border-dashed border-gray-300 p-4 rounded bg-light">
        <div class="row g-3 align-items-center">
          <div class="col-md-9">
            <div class="d-flex flex-column gap-2">
              <!-- Rândul 1: De la + Luna -->
              <div class="d-flex align-items-center gap-4 flex-wrap">
                <div class="d-flex align-items-center gap-2">
                  <label class="mb-0 fw-bold" style="min-width: 60px;">De la:</label>
                  <input type="date" class="form-control form-control-sm" v-model="filters.from" style="width: 140px;" />
                </div>
                <div class="d-flex align-items-center gap-2">
                  <label class="mb-0 fw-bold">Luna:</label>
                  <select class="form-select form-select-sm" v-model.number="selected_month" style="width: 110px;">
                    <option v-for="(name, m) in monthNames" :key="m" :value="m+1">{{ name }}</option>
                  </select>
                </div>
              </div>
              <!-- Rândul 2: Până la + An/Săgeți -->
              <div class="d-flex align-items-center gap-4 flex-wrap mt-2">
                <div class="d-flex align-items-center gap-2">
                  <label class="mb-0 fw-bold" style="min-width: 60px;">Până la:</label>
                  <input type="date" class="form-control form-control-sm" v-model="filters.to" style="width: 140px;" />
                </div>
                <div class="d-flex align-items-center gap-2">
                  <button type="button" class="btn btn-xs btn-icon btn-light-primary" @click="prevMonth" title="Luna anterioară">
                    <i class="fa-solid fa-chevron-up"></i>
                  </button>
                  <button type="button" class="btn btn-xs btn-icon btn-light-primary" @click="nextMonth" title="Luna următoare">
                    <i class="fa-solid fa-chevron-down"></i>
                  </button>
                  <input type="number" class="form-control form-control-sm" v-model.number="selected_year" style="width: 70px;" />
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-3 text-md-end text-center">
            <button class="btn btn-primary w-100" @click="loadData" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              <i class="fa fa-search me-1" v-if="!loading"></i> Accept
            </button>
          </div>
        </div>
      </div>

      <!-- Tab-uri interne Vânzări / Cumpărări / Liste -->
      <ul class="nav nav-tabs nav-line-tabs mb-5 fs-6">
        <li class="nav-item">
          <a class="nav-link text-active-primary py-4" :class="{ 'active': activeTab === 'vanzari' }" 
             href="#" @click.prevent="activeTab = 'vanzari'">
            Jurnal de vânzări (TVA)
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link text-active-primary py-4" :class="{ 'active': activeTab === 'cumparari' }" 
             href="#" @click.prevent="activeTab = 'cumparari'">
            Jurnal de cumpărări (TVA)
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link text-active-success py-4" :class="{ 'active': activeTab === 'lista_iesiri' }" 
             href="#" @click.prevent="activeTab = 'lista_iesiri'">
            Situație Ieșiri
          </a>
        </li>
        <li class="nav-item">
          <a class="nav-link text-active-success py-4" :class="{ 'active': activeTab === 'lista_intrari' }" 
             href="#" @click.prevent="activeTab = 'lista_intrari'">
            Situație Intrări
          </a>
        </li>
      </ul>

      <div v-if="loading" class="py-20 text-center">
        <div class="spinner-border text-primary w-40px h-40px" role="status"></div>
        <div class="text-muted mt-3">Se încarcă datele...</div>
      </div>

      <div v-else-if="!jurnalData" class="py-20 text-center text-muted">
        <p class="fs-5">Alege perioada și apasă "Accept" pentru a genera jurnalul.</p>
      </div>

      <div v-else>
        <!-- SECȚIUNE JURNAL TVA (spart pe cote) -->
        <div v-if="activeTab === 'vanzari' || activeTab === 'cumparari'">
          <!-- Tabel Sumarizare (Sus) -->
          <div class="mb-8">
            <h5 class="fw-bold mb-3 text-uppercase fs-7 text-gray-600">Centralizare pe cote de TVA</h5>
            <div class="table-responsive">
              <table class="table table-bordered table-sm align-middle gs-4 gy-2 border-gray-300">
                <thead class="bg-light-primary text-primary fw-bold text-center fs-8">
                  <tr>
                    <th rowspan="2">Denumire</th>
                    <th rowspan="2">Total document</th>
                    <th rowspan="2">Bază TVA</th>
                    <th rowspan="2">Valoare TVA</th>
                    <th colspan="3" class="bg-light-success text-success">TVA la încasare/plată</th>
                    <th rowspan="2">Neachitat</th>
                    <th colspan="2" class="bg-light-warning text-warning">TVA neexigibilă</th>
                  </tr>
                  <tr>
                    <th class="bg-light-success text-success border-start">Total încasat/plătit</th>
                    <th class="bg-light-success text-success">Bază TVA</th>
                    <th class="bg-light-success text-success">TVA</th>
                    <th class="bg-light-warning text-warning border-start">Bază TVA neex.</th>
                    <th class="bg-light-warning text-warning">TVA neexigibilă</th>
                  </tr>
                </thead>
                <tbody class="fs-8">
                  <tr v-for="s in jurnalData.sumar" :key="s.cota" class="text-end">
                    <td class="text-start fw-bold">{{ s.denumire }}</td>
                    <td>{{ formatVal(s.total) }}</td>
                    <td>{{ formatVal(s.baza) }}</td>
                    <td>{{ formatVal(s.tva) }}</td>
                    <td class="bg-light-success">{{ formatVal(s.total_incasat ?? s.total_platit) }}</td>
                    <td class="bg-light-success">{{ formatVal(s.baza_tva_incas) }}</td>
                    <td class="bg-light-success">{{ formatVal(s.tva_incasare) }}</td>
                    <td>{{ formatVal(s.neachitat) }}</td>
                    <td class="bg-light-warning">{{ formatVal(s.baza_tva_neex) }}</td>
                    <td class="bg-light-warning">{{ formatVal(s.tva_neexigibila) }}</td>
                  </tr>
                </tbody>
                <tfoot class="bg-light fw-bold text-end fs-8">
                  <tr>
                    <td class="text-center fw-bold">TOTAL GENERAL</td>
                    <td>{{ formatVal(jurnalData.total_general.total) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.baza) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.tva) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.total_incasat ?? jurnalData.total_general.total_platit) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.baza_tva_incas) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.tva_incasare) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.neachitat) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.baza_tva_neex) }}</td>
                    <td>{{ formatVal(jurnalData.total_general.tva_neexigibila) }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          <!-- Tabel Detalii (Jos) -->
          <div>
            <h5 class="fw-bold mb-3 text-uppercase fs-7 text-gray-600">Documente detaliate (TVA)</h5>
            <div class="table-responsive" style="max-height: 500px; overflow-y: auto;">
              <table class="table table-bordered table-hover table-sm align-middle gs-3 gy-1 fs-8 border-gray-300">
                <thead class="bg-light sticky-top fs-9">
                  <tr class="text-center fw-bold">
                    <th rowspan="2" style="width: 40px;">Nr. crt.</th>
                    <th rowspan="2">Data</th>
                    <th rowspan="2">Nr. document</th>
                    <th rowspan="2">Client / Furnizor</th>
                    <th rowspan="2">Cod fiscal</th>
                    <th rowspan="2">%</th>
                    <th rowspan="2">Total</th>
                    <th rowspan="2">Bază TVA</th>
                    <th rowspan="2">TVA</th>
                    <th colspan="3" class="bg-light-success">TVA la încasare/plată</th>
                    <th rowspan="2">Neachitat</th>
                    <th colspan="2" class="bg-light-warning">TVA neexigibilă</th>
                  </tr>
                  <tr>
                    <th class="bg-light-success border-start">Total încasat/plătit</th>
                    <th class="bg-light-success">Bază TVA</th>
                    <th class="bg-light-success">TVA</th>
                    <th class="bg-light-warning border-start">Bază TVA neex.</th>
                    <th class="bg-light-warning">TVA neexigibilă</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(d, index) in jurnalData.detalii" :key="d.id + '_' + d.cota_tva" class="text-end">
                    <td class="text-center">{{ index + 1 }}</td>
                    <td class="text-center">{{ d.data_doc }}</td>
                    <td class="text-start">{{ d.serie_doc }} {{ d.nr_doc }}</td>
                    <td class="text-start">{{ d.client_nume || d.furnizor_nume }}</td>
                    <td class="text-center">{{ d.client_cui || d.furnizor_cui }}</td>
                    <td class="text-center">{{ d.cota_tva }}</td>
                    <td>{{ formatVal(d.total) }}</td>
                    <td>{{ formatVal(d.baza) }}</td>
                    <td>{{ formatVal(d.val_tva) }}</td>
                    <td class="bg-light-success">{{ formatVal(d.total_incasat ?? d.total_platit) }}</td>
                    <td class="bg-light-success">{{ formatVal(d.baza_tva_incas) }}</td>
                    <td class="bg-light-success">{{ formatVal(d.tva_incasare) }}</td>
                    <td>{{ formatVal(d.neachitat) }}</td>
                    <td class="bg-light-warning">{{ formatVal(d.baza_tva_neex) }}</td>
                    <td class="bg-light-warning">{{ formatVal(d.tva_neexigibila) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- SECȚIUNE SITUAȚIE FACTURI (Lista Ieșiri / Intrări) -->
        <div v-else>
          <div class="mb-4 d-flex justify-content-between align-items-center">
            <h5 class="fw-bold mb-0 text-uppercase fs-7 text-gray-600">
              Situație Facturi ({{ activeTab === 'lista_iesiri' ? 'Clienți' : 'Furnizori' }})
            </h5>
          </div>
          <div class="table-responsive" style="max-height: 600px; overflow-y: auto;">
            <table class="table table-bordered table-hover table-sm align-middle gs-3 gy-2 fs-8 border-gray-300">
              <thead class="bg-light sticky-top fs-8">
                <tr class="text-center fw-bold">
                  <th style="width: 40px;">Nr. crt.</th>
                  <th>Data</th>
                  <th>Scadent</th>
                  <th>Nr. document</th>
                  <th>Client / Furnizor</th>
                  <th>Valoare (fără TVA)</th>
                  <th>TVA</th>
                  <th>Total Factură</th>
                  <th class="bg-light-danger text-danger">Neachitat</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(d, index) in jurnalData.detalii" :key="d.id" class="text-end">
                  <td class="text-center">{{ index + 1 }}</td>
                  <td class="text-center">{{ d.data_doc }}</td>
                  <td class="text-center" :class="{'text-danger': isOverdue(d.data_scadenta, d.neachitat)}">{{ d.data_scadenta }}</td>
                  <td class="text-start fw-bold">{{ d.serie_doc }} {{ d.nr_doc }}</td>
                  <td class="text-start">{{ d.client_nume || d.furnizor_nume }} <br><span class="text-muted fs-9">{{ d.client_cui || d.furnizor_cui }}</span></td>
                  <td>{{ formatVal(d.valoare) }}</td>
                  <td>{{ formatVal(d.tva) }}</td>
                  <td class="fw-bold">{{ formatVal(d.total) }}</td>
                  <td class="fw-bold text-danger bg-light-danger">{{ formatVal(d.neachitat) }}</td>
                </tr>
              </tbody>
              <tfoot class="bg-light fw-bold text-end fs-7">
                <tr>
                  <td colspan="5" class="text-center">TOTAL GENERAL</td>
                  <td>{{ formatVal(jurnalData.total_general.valoare) }}</td>
                  <td>{{ formatVal(jurnalData.total_general.tva) }}</td>
                  <td>{{ formatVal(jurnalData.total_general.total) }}</td>
                  <td class="text-danger bg-light-danger">{{ formatVal(jurnalData.total_general.neachitat) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';

export default {
  name: 'JurnalTva',
  props: {
    firmaId: {
      type: Number,
      required: true
    }
  },
  data() {
    const today = new Date();
    return {
      loading: false,
      activeTab: 'vanzari',
      jurnalData: null,
      filters: {
        from: '',
        to: ''
      },
      selected_month: today.getMonth() + 1,
      selected_year: today.getFullYear(),
      monthNames: [
        'Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie',
        'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'
      ]
    }
  },
  mounted() {
    this.autoSetDates();
  },
  methods: {
    autoSetDates() {
      const y = this.selected_year;
      const m = this.selected_month;
      const firstDay = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDayDate = new Date(y, m, 0);
      const lastDay = `${y}-${String(m).padStart(2, '0')}-${String(lastDayDate.getDate()).padStart(2, '0')}`;
      this.filters.from = firstDay;
      this.filters.to = lastDay;
    },
    prevMonth() {
      if (this.selected_month === 1) {
        this.selected_month = 12;
        this.selected_year--;
      } else {
        this.selected_month--;
      }
    },
    nextMonth() {
      if (this.selected_month === 12) {
        this.selected_month = 1;
        this.selected_year++;
      } else {
        this.selected_month++;
      }
    },
    async loadData() {
      this.loading = true;
      this.jurnalData = null;
      try {
        let endpoint = '';
        if (this.activeTab === 'vanzari') endpoint = '/api/contabilitate/jurnal-tva/vanzari';
        else if (this.activeTab === 'cumparari') endpoint = '/api/contabilitate/jurnal-tva/cumparari';
        else if (this.activeTab === 'lista_iesiri') endpoint = '/api/contabilitate/jurnal-tva/lista-iesiri';
        else if (this.activeTab === 'lista_intrari') endpoint = '/api/contabilitate/jurnal-tva/lista-intrari';

        const res = await axios.get(endpoint, {
          params: {
            id_firma: this.firmaId,
            from: this.filters.from,
            to: this.filters.to
          }
        });
        this.jurnalData = res.data;
      } catch (e) {
        console.error("Eroare la încărcarea jurnalului", e);
        Swal.fire('Eroare', 'Nu s-au putut prelua datele.', 'error');
      } finally {
        this.loading = false;
      }
    },
    async exportPDF(mode = 'save') {
      try {
        Swal.fire({
          title: mode === 'preview' ? 'Se deschide PDF-ul...' : 'Se generează PDF-ul...',
          allowOutsideClick: false,
          didOpen: () => { Swal.showLoading(); }
        });

        let endpoint = '';
        let typeName = '';
        if (this.activeTab === 'vanzari') { endpoint = '/api/contabilitate/jurnal-tva/vanzari/pdf'; typeName = 'Jurnal_Vanzari'; }
        else if (this.activeTab === 'cumparari') { endpoint = '/api/contabilitate/jurnal-tva/cumparari/pdf'; typeName = 'Jurnal_Cumparari'; }
        else if (this.activeTab === 'lista_iesiri') { endpoint = '/api/contabilitate/jurnal-tva/lista-iesiri/pdf'; typeName = 'Lista_Iesiri'; }
        else if (this.activeTab === 'lista_intrari') { endpoint = '/api/contabilitate/jurnal-tva/lista-intrari/pdf'; typeName = 'Lista_Intrari'; }

        const response = await axios.get(endpoint, {
          params: {
            id_firma: this.firmaId,
            from: this.filters.from,
            to: this.filters.to
          },
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        
        if (mode === 'preview') {
            window.open(url, '_blank');
        } else {
            const link = document.createElement('a');
            link.href = url;
            const fileName = `${typeName}_${this.filters.from}_${this.filters.to}.pdf`;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        Swal.close();
      } catch (e) {
        console.error("Eroare la export PDF", e);
        Swal.fire('Eroare', 'Nu s-a putut genera fișierul PDF.', 'error');
      }
    },
    formatVal(val) {
      if (val === undefined || val === null) return '0,00';
      return Number(val).toLocaleString('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    isOverdue(scadenta, neachitat) {
      if (!scadenta || neachitat <= 0) return false;
      // Convert dd.mm.yyyy to Date object
      const parts = scadenta.split('.');
      if (parts.length === 3) {
        const scDate = new Date(parts[2], parts[1] - 1, parts[0]);
        const today = new Date();
        today.setHours(0,0,0,0);
        return scDate < today;
      }
      return false;
    }
  },
  watch: {
    selected_month() { this.autoSetDates(); },
    selected_year() { this.autoSetDates(); },
    activeTab() { if (this.jurnalData) this.loadData(); }
  }
}
</script>

<style scoped>
.table th {
  font-size: 0.8rem;
  background-color: #f8f9fa;
}
.table td {
  font-size: 0.8rem;
}
.sticky-top {
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;
}
</style>
